import PaymentMethodUsed from '@/components/payment-methods/PaymentMethodUsed';
import PayoutBatchStatusBadge from '@/components/payouts/PayoutBatchStatusBadge';
import { Money } from '@/components/text';
import ClipboardBadge from '@/components/ui/clipboard/ClipboardBadge';
import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import type { Payout } from '@/types/payout';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface PayoutDetailsSectionProps {
  payout: Payout;
}

const PayoutDetailsSection: React.FC<PayoutDetailsSectionProps> = ({ payout }) => {
  const { t } = useTranslation();

  return (
    <DataListRoot orientation="horizontal" width="full" divideY="1px" gapY={1}>
      <DataListItem
        py={2}
        label={t('payouts.details.transactionId')}
        fontSize="15px"
        labelMinW="160px"
        itemValueProps={{ justifyContent: 'flex-end' }}
        value={
          <ClipboardBadge
            value={payout.id}
            badgeProps={{
              size: 'sm',
            }}
          >
            {payout.id}
          </ClipboardBadge>
        }
      />
      <DataListItem
        py={2}
        label={t('payouts.details.transactionDate')}
        fontSize="15px"
        labelMinW="160px"
        itemValueProps={{ justifyContent: 'flex-end' }}
        value={DateTime.fromISO(payout.createdAt).toFormat('dd MMM, hh:mm a')}
      />
      <DataListItem
        py={2}
        label={t('payouts.details.status')}
        fontSize="15px"
        labelMinW="160px"
        itemValueProps={{ justifyContent: 'flex-end' }}
        value={
          <PayoutBatchStatusBadge
            status={payout.status}
            fundedAt={payout.fundedAt}
            lastPaymentFailedAt={payout.lastPaymentFailedAt}
            closedReason={payout.closedReason}
            size="sm"
            fontSize="10px"
          />
        }
      />
      <DataListItem
        py={2}
        label={t('payouts.details.paymentMethod')}
        labelMinW="160px"
        itemValueProps={{ justifyContent: 'flex-end' }}
        value={
          <PaymentMethodUsed
            paymentMethod={payout.paymentMethod}
            fontSize="15px"
            fontWeight="medium"
          />
        }
      />

      <DataListItem
        py={2}
        label={t('payouts.details.totalPaidCommission')}
        labelMinW="160px"
        itemValueProps={{ justifyContent: 'flex-end' }}
        value={
          payout.totalCommissionAmount ? (
            <Money
              fontSize="15px"
              value={payout.totalCommissionAmount.amount}
              currency={payout.totalCommissionAmount.currencyCode}
            />
          ) : (
            '—'
          )
        }
      />
      <DataListItem
        py={2}
        label={t('payouts.details.totalPlatformFees')}
        labelMinW="160px"
        itemValueProps={{ justifyContent: 'flex-end' }}
        value={
          payout.totalCollectedPlatformFee ? (
            <Money
              fontSize="15px"
              value={payout.totalCollectedPlatformFee.amount}
              currency={payout.totalCollectedPlatformFee.currencyCode}
            />
          ) : (
            '—'
          )
        }
      />
      <DataListItem
        py={2}
        label={t('payouts.details.totalChargedAmount')}
        labelMinW="160px"
        itemValueProps={{ justifyContent: 'flex-end' }}
        value={
          <Money
            fontSize="15px"
            value={payout.totalChargeAmount.amount}
            currency={payout.totalChargeAmount.currencyCode}
          />
        }
      />
    </DataListRoot>
  );
};

export default PayoutDetailsSection;
